<script>
import {
  BModal, BSpinner, BFormInput, BInputGroup, BInputGroupPrepend, BButton,
} from 'bootstrap-vue';
import SLS_pedidosVendasService from '@/services/Operacoes/sls_pedidoVendas.service';

export default {
  name: 'DraftOrdersModal',
  components: {
    BModal,
    BSpinner,
    BFormInput,
    BInputGroup,
    BInputGroupPrepend,
    BButton,
  },
  data() {
    return {
      isOpen: false,
      isLoading: false,
      hasError: false,
      errorMessage: '',
      searchQuery: '',
      drafts: [],
    };
  },
  computed: {
    filteredDrafts() {
      if (!this.searchQuery.trim()) {
        return this.drafts;
      }
      const query = this.searchQuery.toLowerCase().trim();
      return this.drafts.filter((draft) => {
        const prePedido = String(draft.pedidoId || '').toLowerCase();
        const pedidoErp = String(draft.pedidoNumeroIntegracao || '').toLowerCase();
        const cliente = String(draft.clienteRazaoSocial || '').toLowerCase();
        const clienteCodigo = String(draft.clienteIdERP || '').toLowerCase();
        const data = String(draft.pedidoData || '').toLowerCase();
        return prePedido.includes(query)
               || pedidoErp.includes(query)
               || cliente.includes(query)
               || clienteCodigo.includes(query)
               || data.includes(query);
      });
    },
    draftCount() {
      return this.drafts.length;
    },
  },
  methods: {
    openModal() {
      this.isOpen = true;
      this.searchQuery = '';
      this.loadDrafts();
    },
    closeModal() {
      this.isOpen = false;
    },
    async loadDrafts() {
      this.isLoading = true;
      this.hasError = false;
      this.errorMessage = '';

      try {
        const perfilLogado = JSON.parse(localStorage.getItem('perfilLogado'));
        const perfil = perfilLogado?.perfil_acesso_nome || 'Representante';

        let representanteId = null;
        if (perfil === 'Representante') {
          const representanteData = JSON.parse(localStorage.getItem('representanteData'));
          representanteId = representanteData?.representanteid;
        }

        const params = {};
        if (representanteId) {
          params.perfilTipo = perfil;
          params.perfilId = representanteId;
        }

        const slsPedidoVendasService = new SLS_pedidosVendasService();
        const result = await slsPedidoVendasService.getRascunhosPedidosVendas(params);

        this.drafts = result?.data || result || [];

        this.$emit('drafts-loaded', this.drafts.length);
      } catch (error) {
        console.error('Erro ao carregar rascunhos:', error);
        this.hasError = true;
        this.errorMessage = error.message || 'Erro ao carregar rascunhos';
        this.drafts = [];
      } finally {
        this.isLoading = false;
      }
    },
    selectDraft(draft) {
      const { pedidoId } = draft;
      const clienteId = draft.clienteId || 0;

      this.closeModal();

      this.$router.push({
        path: `/b2bhub/operacoes/pedido-vendas/detalhes/${pedidoId}/${clienteId}`,
      });
    },
    formatCurrency(value) {
      if (!value && value !== 0) return 'R$ 0,00';
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      }).format(value);
    },
    formatDate(dateStr) {
      if (!dateStr) return '-';
      return dateStr;
    },
  },
  mounted() {
    this.$root.$on('draft:updated', () => {
      if (this.isOpen) {
        this.loadDrafts();
      }
    });

    this.$root.$on('draft:created', () => {
      if (this.isOpen) {
        this.loadDrafts();
      }
    });
  },
  beforeDestroy() {
    this.$root.$off('draft:updated');
    this.$root.$off('draft:created');
  },
};
</script>

<template>
  <b-modal
    v-model="isOpen"
    modal-class="draft-orders-modal-b2b"
    content-class="modal-content-b2b"
    size="lg"
    hide-header
    hide-footer
    no-close-on-backdrop
    @hidden="closeModal"
  >
    <div class="modal-wrapper-b2b">
      <div class="modal-header-b2b">
        <div class="header-left">
          <div class="header-icon">
            <feather-icon icon="FileTextIcon" size="16" />
          </div>
          <h3 class="header-title">Rascunhos</h3>
          <span v-if="!isLoading && draftCount > 0" class="draft-count-badge">{{ draftCount }}</span>
        </div>
        <button type="button" class="close-btn" @click="closeModal" aria-label="Fechar">
          <feather-icon icon="XIcon" size="16" />
        </button>
      </div>

      <div class="modal-body-b2b">
        <div class="search-container">
          <b-input-group class="search-input-group">
            <b-input-group-prepend>
              <span class="search-icon-wrapper">
                <feather-icon icon="SearchIcon" size="14" />
              </span>
            </b-input-group-prepend>
            <b-form-input
              v-model="searchQuery"
              class="search-input"
              placeholder="Buscar por nº pré-pedido, pedido ERP, cliente, data..."
              @keydown.esc="closeModal"
            />
          </b-input-group>
        </div>

        <div class="drafts-list-container">
          <div v-if="isLoading" class="loading-state">
            <div class="skeleton-item" v-for="n in 4" :key="n">
              <div class="skeleton-header">
                <div class="skeleton-line short"></div>
                <div class="skeleton-badge"></div>
              </div>
              <div class="skeleton-line medium"></div>
              <div class="skeleton-line long"></div>
            </div>
          </div>

          <div v-else-if="hasError" class="error-state">
            <feather-icon icon="AlertCircleIcon" size="32" class="error-icon" />
            <p class="error-message">{{ errorMessage }}</p>
            <button type="button" class="retry-btn" @click="loadDrafts">
              <feather-icon icon="RefreshCwIcon" size="14" />
              Tentar novamente
            </button>
          </div>

          <div v-else-if="filteredDrafts.length === 0" class="empty-state">
            <feather-icon icon="InboxIcon" size="40" class="empty-icon" />
            <p class="empty-title">Nenhum rascunho encontrado</p>
            <p class="empty-subtitle" v-if="searchQuery">Tente ajustar os termos da busca</p>
            <p class="empty-subtitle" v-else>Seus pedidos em rascunho aparecerão aqui</p>
          </div>

          <div v-else class="drafts-list">
            <div
              v-for="draft in filteredDrafts"
              :key="draft.pedidoId"
              class="draft-item"
              @click="selectDraft(draft)"
              tabindex="0"
              @keydown.enter="selectDraft(draft)"
            >
              <div class="draft-header">
                <div class="draft-id-group">
                  <span class="draft-pre-pedido">#{{ draft.pedidoId }}</span>
                  <span class="draft-status-badge">Rascunho</span>
                </div>
                <feather-icon icon="ChevronRightIcon" size="16" class="draft-arrow" />
              </div>
              <div class="draft-details">
                <span class="draft-erp" v-if="draft.pedidoNumeroIntegracao && draft.pedidoNumeroIntegracao !== 'RASCUNHO'">
                  Pedido ERP: {{ draft.pedidoNumeroIntegracao }}
                </span>
                <span class="draft-date">{{ formatDate(draft.pedidoData) }}</span>
                <span class="draft-value">{{ draft.pedidoValorTotal || 'R$ 0,00' }}</span>
              </div>
              <div class="draft-client">
                <feather-icon icon="UserIcon" size="12" class="client-icon" />
                <span class="client-name">{{ draft.clienteRazaoSocial || 'Cliente não informado' }}</span>
                <span class="client-code" v-if="draft.clienteIdERP">({{ draft.clienteIdERP }})</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="modal-footer-b2b">
        <div class="footer-right">
          <b-button variant="secondary" @click="closeModal">
            Fechar
          </b-button>
        </div>
      </div>
    </div>
  </b-modal>
</template>

<style lang="scss">
.draft-orders-modal-b2b {
  .modal-lg {
    max-width: 640px !important;
  }

  .modal-content {
    border: 1px solid var(--border-color, #e5e7eb);
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  }

  .modal-body {
    padding: 0;
  }
}

.draft-orders-modal-b2b .modal-wrapper-b2b {
  background: var(--sf-card-bg);
  color: var(--sf-card-text);
}

.draft-orders-modal-b2b .modal-header-b2b {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.875rem 1.25rem;
  border-bottom: 1px solid var(--sf-card-border);
}

.draft-orders-modal-b2b .modal-header-b2b .header-left {
  display: flex;
  align-items: center;
  gap: 0.625rem;
}

.draft-orders-modal-b2b .modal-header-b2b .header-icon {
  width: 32px;
  height: 32px;
  background: var(--sf-accent);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    color: #fff;
  }
}

.draft-orders-modal-b2b .modal-header-b2b .header-title {
  font-size: 0.9375rem;
  font-weight: 600;
  color: var(--sf-surface-text-dark);
  margin: 0;
}

.draft-orders-modal-b2b .draft-count-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  background: var(--sf-accent);
  color: #fff;
  border-radius: 10px;
  font-size: 0.6875rem;
  font-weight: 600;
}

.draft-orders-modal-b2b .modal-header-b2b .close-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: 1px solid var(--sf-card-border);
  background: var(--sf-card-bg);
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s;
  color: var(--sf-surface-muted);

  &:hover {
    background: var(--sf-surface-bg);
    color: var(--sf-surface-text);
  }
}

.draft-orders-modal-b2b .modal-body-b2b {
  padding: 0;
}

.draft-orders-modal-b2b .search-container {
  padding: 0.875rem 1.25rem;
  border-bottom: 1px solid var(--sf-card-border);
}

.draft-orders-modal-b2b .search-input-group {
  .search-icon-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 0.75rem;
    background: var(--sf-surface-bg);
    border: 1px solid var(--sf-card-border);
    border-right: none;
    border-radius: 6px 0 0 6px;
    color: var(--sf-surface-muted);
  }

  .search-input {
    background: var(--sf-form-bg) !important;
    border: 1px solid var(--sf-form-border) !important;
    border-radius: 0 6px 6px 0 !important;
    color: var(--sf-form-text) !important;
    font-size: 0.8125rem !important;
    height: 36px !important;

    &::placeholder {
      color: var(--sf-surface-muted) !important;
    }

    &:focus {
      background: var(--sf-form-bg) !important;
      border-color: var(--sf-accent) !important;
      box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.15) !important;
    }
  }
}

.draft-orders-modal-b2b .drafts-list-container {
  max-height: 55vh;
  overflow-y: auto;
  padding: 0.75rem 1.25rem;
}

.draft-orders-modal-b2b .loading-state {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.draft-orders-modal-b2b .skeleton-item {
  background: var(--sf-surface-bg);
  border: 1px solid var(--sf-card-border);
  border-radius: 8px;
  padding: 0.75rem;
}

.draft-orders-modal-b2b .skeleton-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}

.draft-orders-modal-b2b .skeleton-line {
  height: 12px;
  background: linear-gradient(90deg, var(--sf-card-border) 25%, var(--sf-surface-bg) 50%, var(--sf-card-border) 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;

  &.short { width: 60px; }
  &.medium { width: 120px; margin-bottom: 0.375rem; }
  &.long { width: 200px; }
}

.draft-orders-modal-b2b .skeleton-badge {
  width: 60px;
  height: 18px;
  background: linear-gradient(90deg, var(--sf-card-border) 25%, var(--sf-surface-bg) 50%, var(--sf-card-border) 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
}

@keyframes shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

.draft-orders-modal-b2b .error-state,
.draft-orders-modal-b2b .empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2.5rem 1rem;
  text-align: center;
}

.draft-orders-modal-b2b .error-icon {
  color: #ef4444;
  margin-bottom: 0.75rem;
}

.draft-orders-modal-b2b .empty-icon {
  color: var(--sf-surface-muted);
  margin-bottom: 0.75rem;
}

.draft-orders-modal-b2b .error-message {
  font-size: 0.8125rem;
  color: var(--sf-surface-muted);
  margin-bottom: 1rem;
}

.draft-orders-modal-b2b .empty-title {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--sf-surface-text-dark);
  margin-bottom: 0.25rem;
}

.draft-orders-modal-b2b .empty-subtitle {
  font-size: 0.8125rem;
  color: var(--sf-surface-muted);
  margin: 0;
}

.draft-orders-modal-b2b .retry-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.5rem 1rem;
  border: 1px solid var(--sf-card-border);
  background: var(--sf-card-bg);
  color: var(--sf-surface-text);
  border-radius: 6px;
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    background: var(--sf-surface-bg);
    border-color: var(--sf-accent);
  }
}

.draft-orders-modal-b2b .drafts-list {
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
}

.draft-orders-modal-b2b .draft-item {
  background: var(--sf-surface-bg);
  border: 1px solid var(--sf-card-border);
  border-radius: 8px;
  padding: 0.75rem 1rem;
  cursor: pointer;
  transition: all 0.15s;

  &:hover, &:focus {
    background: var(--sf-card-bg);
    border-color: var(--sf-accent);
    outline: none;

    .draft-arrow {
      color: var(--sf-accent);
      transform: translateX(2px);
    }
  }
}

.draft-orders-modal-b2b .draft-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.375rem;
}

.draft-orders-modal-b2b .draft-id-group {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.draft-orders-modal-b2b .draft-pre-pedido {
  font-size: 0.9375rem;
  font-weight: 700;
  color: var(--sf-surface-text-dark);
}

.draft-orders-modal-b2b .draft-status-badge {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  background: var(--sf-warning-bg, rgba(234, 179, 8, 0.15));
  color: var(--sf-warning-text, #b45309);
  border-radius: 4px;
  font-size: 0.625rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.draft-orders-modal-b2b .draft-arrow {
  color: var(--sf-surface-muted);
  transition: all 0.15s;
}

.draft-orders-modal-b2b .draft-details {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.375rem;
  font-size: 0.75rem;
  color: var(--sf-surface-muted);
}

.draft-orders-modal-b2b .draft-erp {
  font-weight: 500;
}

.draft-orders-modal-b2b .draft-value {
  font-weight: 600;
  color: var(--sf-success, #10b981);
}

.draft-orders-modal-b2b .draft-client {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.75rem;
  color: var(--sf-surface-muted);
}

.draft-orders-modal-b2b .client-icon {
  color: var(--sf-surface-muted);
}

.draft-orders-modal-b2b .client-name {
  font-weight: 500;
  color: var(--sf-surface-text-dark);
}

.draft-orders-modal-b2b .client-code {
  color: var(--sf-surface-muted);
}

.draft-orders-modal-b2b .modal-footer-b2b {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 0.875rem 1.25rem;
  border-top: 1px solid var(--sf-card-border);
}

.draft-orders-modal-b2b .footer-right {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

/* Botão do footer agora usa <b-button variant="secondary"> e respeita tokens globais */

/* Overrides específicos de dark removidos; tokens --sf-* garantem contraste no tema escuro */
</style>
